import FeatherIcon from "feather-icons-react";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useCallback } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Modal,
  Col,
  Row,
  Spinner,
  Input,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Form,
  Badge,
} from "reactstrap";
import api from "../helpers/api";
import { translateActivity } from "../helpers/activityTranslator";
import { formatCreatedDate, formatEnumName, urgencyColors, workstatusOptions } from "../constants/utils";
import { getPresignedUrl } from "../helpers/fileUpload";
import PermissionGuard from "../components/Common/PermissionGuard";
import usePermission from "../Hooks/usePermission";
import ServiceRequestModal from "../components/ServiceRequestModal";
import AssetModal from "../components/AssetModal";
import LocationModal from "../components/LocationModal";
import ApproveServiceRequestModal from "../components/ApproveServiceRequestModal";
import DeclineServiceRequestModal from "../components/DeclineServiceRequestModal";
import { useTranslation } from 'react-i18next';

const ServiceRequests = () => {
  const { t } = useTranslation();
  document.title = t('shared.service_requests', 'Service Requests') + ' | ArcMedix';

  const currentUser = JSON.parse(localStorage.getItem('authUser'));
  const organizationId = currentUser?.activeOrganizationId ?? null;
  const { isAdmin, hasPermission } = usePermission();

  // State
  const [serviceRequests, setServiceRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [createModal, setCreateModal] = useState(false);
  const [detailsModal, setDetailsModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [count, setCount] = useState(10);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Activity state
  const [activeDetailsTab, setActiveDetailsTab] = useState('details');
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  // Approve/Decline state
  const [approveModal, setApproveModal] = useState(false);
  const [declineModal, setDeclineModal] = useState(false);
  const [technicians, setTechnicians] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);

  // Filter state
  const [statusFilter, setStatusFilter] = useState("PENDING");
  const [locations, setLocations] = useState([]);
  const [assets, setAssets] = useState([]);
  const [enumData, setEnumData] = useState([]);

  // Asset & Location modal state
  const [assetModal, setAssetModal] = useState({
    isOpen: false,
    userData: null,
    loading: false
  });
  const [locationModal, setLocationModal] = useState({
    isOpen: false,
    type: null,
    title: '',
    mainData: null,
    relatedData: [],
    loading: false
  });

  // Toggle status tab
  const toggleTab = (tab) => {
    if (statusFilter !== tab) {
      setStatusFilter(tab);
      setServiceRequests([]);
      setCount(10);
      setHasMore(true);
    }
  };

  // Check if user can modify a request (owner only for non-admin)
  const canModifyRequest = (request) => {
    if (isAdmin) return true;
    const requestedById = request?.requestedBy?.id ?? request?.requestedBy;
    return requestedById === currentUser?.id;
  };

  // Fetch dynamic enums (for priority/usage status options)
  const fetchEnums = async () => {
    try {
      const { data } = await api.get("dynamic-enum/get-all");
      setEnumData(data.response || []);
    } catch (error) {
      console.error("Error fetching enums:", error);
    }
  };

  // Fetch locations for dropdown
  const fetchLocations = useCallback(async () => {
    try {
      const { data } = await api.get(
        `organization-location/get-by-organization-id?organizationId=${organizationId}&count=9999&hierarchy=false`
      );
      setLocations(data?.response?.[0]?.locations || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  }, [organizationId]);

  // Fetch assets for dropdown
  const fetchAssets = useCallback(async () => {
    try {
      const { data } = await api.get(`asset/all?organizationId=${organizationId}&hierarchy=false`);
      setAssets(data?.response?.[0]?.assets || []);
    } catch (error) {
      console.error('Error fetching assets:', error);
    }
  }, [organizationId]);

  // Fetch service requests
  const fetchServiceRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter && statusFilter !== 'ALL') params.append('status', statusFilter);
      params.append('count', count);

      // Use /all for admins, /my-requests for regular users
      const endpoint = isAdmin
        ? `service-request/all?${params.toString()}`
        : `service-request/my-requests?${params.toString()}`;

      const { data } = await api.get(endpoint);
      setServiceRequests(data?.response || []);
      setHasMore((data?.response?.length || 0) >= count);
    } catch (error) {
      console.error('Error fetching service requests:', error);
    } finally {
      setLoading(false);
    }
  }, [count, searchTerm, statusFilter, isAdmin]);

  // Load more
  const loadMore = async () => {
    if (loading || !hasMore) return;
    setCount(prev => prev + 10);
  };

  // Toggle modals
  const toggleCreateModal = (request = null) => {
    setCreateModal(!createModal);
    setSelectedRequest(request || {});
    setErrors({});
    setApiError("");
  };

  const toggleDetailsModal = (request = null) => {
    setDetailsModal(!detailsModal);
    setSelectedRequest(request);
    setActiveDetailsTab('details');
    setActivities([]);
    setCommentText('');
  };

  // Fetch service request activities
  const getServiceRequestActivity = async () => {
    if (!selectedRequest?.id) return;
    try {
      setLoadingActivities(true);
      const { data } = await api.get(`service-request/activity?parentId=${selectedRequest.id}`);
      setActivities(data.response || []);
    } catch (error) {
      console.error('Error fetching activities:', error);
      setActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  // Submit a comment
  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || !selectedRequest?.id) return;
    try {
      setSubmittingComment(true);
      await api.patch('service-request/comment', {
        parentId: selectedRequest.id,
        activity: commentText.trim()
      });
      setCommentText('');
      await getServiceRequestActivity();
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setSubmittingComment(false);
    }
  };

  // Enter to submit, Shift+Enter for new line
  const handleCommentKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCommentSubmit(e);
    }
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCount(10);
    setHasMore(true);
  };

  // Handle create/update
  const handleSubmit = async (requestData) => {
    setIsSubmitting(true);
    setApiError("");
    setErrors({});

    // Validation
    const newErrors = {};
    if (!requestData?.title?.trim()) {
      newErrors.title = t("shared.title_required", "Title is required");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      const payload = {
        title: requestData.title,
        assetId: requestData.assetId || null,
        locationId: requestData.locationId || null,
        description: requestData.description || "",
        urgencyLevel: requestData.urgencyLevel || null,
        usageStatus: requestData.usageStatus || null,
        downtimeReasonId: requestData.usageStatus === 'OFFLINE' ? (requestData.downtimeReasonId || null) : null,
        imageUrls: requestData.imageUrls || [],
        documentUrls: requestData.documentUrls || [],
      };

      if (requestData.id) {
        // Update
        payload.id = requestData.id;
        await api.put("service-request/update", payload);
      } else {
        // Create
        await api.post("service-request/create", payload);
      }

      setCreateModal(false);
      setSelectedRequest(null);
      fetchServiceRequests();
    } catch (error) {
      console.error("Error submitting service request:", error);
      setApiError(error?.response?.data?.message || "An error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    try {
      if (selectedRequest?.id) {
        await api.delete(`service-request/delete?id=${selectedRequest.id}`);
        fetchServiceRequests();
        setDeleteModal(false);
        setSelectedRequest(null);
      }
    } catch (error) {
      console.error("Error deleting service request:", error);
      setApiError(error?.response?.data?.message || "Failed to delete");
    }
  };

  // Fetch technicians (admin only)
  const fetchTechnicians = useCallback(async () => {
    try {
      const { data } = await api.get(`user/technicians?organizationId=${organizationId}`);
      setTechnicians(data?.response || []);
    } catch (error) {
      console.error('Error fetching technicians:', error);
    }
  }, [organizationId]);

  // Fetch categories (admin only)
  const fetchCategories = useCallback(async () => {
    try {
      const { data } = await api.get(`organization-category/getAll?organizationId=${organizationId}`);
      setCategories(data?.response || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, [organizationId]);

  // Fetch vendors (admin only)
  const fetchVendors = useCallback(async () => {
    try {
      const { data } = await api.get('vendor/getAllVendors?count=9999');
      setVendors(data?.response || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  }, []);

  // Handle approve
  const handleApprove = async (payload) => {
    setIsSubmitting(true);
    setApiError("");
    try {
      await api.post("service-request/approve", payload);
      setApproveModal(false);
      setSelectedRequest(null);
      fetchServiceRequests();
    } catch (error) {
      console.error("Error approving service request:", error);
      setApiError(error?.response?.data?.message || "Failed to approve request");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle decline
  const handleDecline = async (payload) => {
    setIsSubmitting(true);
    setApiError("");
    try {
      await api.post("service-request/decline", payload);
      setDeclineModal(false);
      setSelectedRequest(null);
      fetchServiceRequests();
    } catch (error) {
      console.error("Error declining service request:", error);
      setApiError(error?.response?.data?.message || "Failed to decline request");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Service request status options (matching Work Orders pattern)
  const srStatusOptions = {
    PENDING:  { label: t("shared.pending", "Pending"),  icon: 'clock',        colorClass: 'bg-warning text-white', borderColor: '#ffc107' },
    APPROVED: { label: t("shared.approved", "Approved"), icon: 'check-circle',  colorClass: 'bg-success text-white', borderColor: '#28a745' },
    DECLINED: { label: t("shared.declined", "Declined"), icon: 'x-circle',      colorClass: 'bg-danger text-white', borderColor: '#dc3545' },
    DONE:     { label: t("shared.done", "Done"),     icon: 'check-square',  colorClass: 'bg-info text-white', borderColor: '#0dcaf0' },
  };

  const getStatusBadge = (status) => {
    const option = srStatusOptions[status] || {
      label: status,
      icon: 'help-circle',
      colorClass: 'bg-secondary text-white',
      borderColor: '#6c757d',
    };
    return (
      <span
        className={`badge rounded-pill py-1 px-2 d-inline-flex align-items-center gap-1 text-uppercase ${option.colorClass}`}
        style={{ border: `1px solid ${option.borderColor}` }}
      >
        <FeatherIcon icon={option.icon} size={12} />
        {option.label}
      </span>
    );
  };

  // Render WO status badge with icon (matching Work Orders page)
  const getWoStatusBadge = (status) => {
    const option = workstatusOptions(t).find(opt => opt.value === status) || {
      label: status,
      icon: 'help-circle',
      colorClass: 'bg-secondary text-white',
      borderColor: '#6c757d',
    };
    return (
      <span
        className={`badge rounded-pill py-1 px-2 d-inline-flex align-items-center gap-1 ${option.colorClass}`}
        style={{ border: `1px solid ${option.borderColor}` }}
      >
        <FeatherIcon icon={option.icon} size={12} />
        {option.label.toUpperCase()}
      </span>
    );
  };

  // Handle asset click - open AssetModal
  const handleAssetClick = async (assetId, cachedAsset = null) => {
    if (!assetId) return;
    setAssetModal(prev => ({ ...prev, isOpen: true, userData: null, loading: true }));
    try {
      const { data: assetData } = await api.get(`asset/get-by-id?id=${assetId}`);
      const { data: issuesData } = await api.get('asset-issues/organization/filtered', {
        params: { assetId, organizationId }
      });
      setAssetModal(prev => ({
        ...prev,
        userData: { ...assetData.response, reportedIssues: issuesData.response || [] },
        loading: false
      }));
    } catch (error) {
      console.error('Error fetching asset details:', error);
      if (cachedAsset) {
        setAssetModal({ isOpen: true, userData: { ...cachedAsset, record: false }, loading: false });
      } else {
        setAssetModal(prev => ({ ...prev, loading: false }));
      }
    }
  };

  // Handle location click - open DetailsModal
  const handleLocationClick = async (locationName) => {
    if (!locationName) return;
    setLocationModal(prev => ({
      ...prev,
      isOpen: true,
      type: 'location',
      title: 'Location Details',
      mainData: locationName,
      relatedData: [],
      loading: true
    }));
    try {
      const { data: response } = await api.get('asset-issues/organization/filtered', {
        params: { organizationLocation: String(locationName), organizationId }
      });
      setLocationModal(prev => ({
        ...prev,
        relatedData: response.response || [],
        loading: false
      }));
    } catch (error) {
      console.error('Error fetching location details:', error);
      setLocationModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Fetch activities when activity tab is selected
  useEffect(() => {
    if (detailsModal && activeDetailsTab === 'activity') {
      getServiceRequestActivity();
    }
  }, [detailsModal, activeDetailsTab]);

  // Effects
  useEffect(() => {
    fetchServiceRequests();
  }, [fetchServiceRequests]);

  useEffect(() => {
    fetchEnums();
    fetchLocations();
    if (isAdmin) {
      fetchTechnicians();
      fetchCategories();
      fetchVendors();
    }
  }, [fetchLocations, fetchTechnicians, fetchCategories, fetchVendors, isAdmin]);

  // Render activity tab content (matching TicketDetailsModal pattern)
  const renderActivityTab = () => (
    <div className="p-4">
      <Card className="border-0 shadow-sm">
        <CardBody className="p-4">
          <div className="d-flex align-items-center mb-4">
            <div className="info-icon me-3">
              <div className="bg-info bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                   style={{ width: '40px', height: '40px' }}>
                <FeatherIcon icon="activity" size={20} className="text-info" />
              </div>
            </div>
            <div>
              <h5 className="mb-1 fw-bold">{t("shared.activity_timeline", "Activity Timeline")}</h5>
              <p className="text-muted small mb-0">{t("business.components.service_requests.complete_history", "Complete history of ticket activities")}</p>
            </div>
          </div>

          {loadingActivities ? (
            <div className="text-center py-5">
              <Spinner color="primary" size="lg" />
              <p className="text-muted mt-3">{t("shared.loading_activity_log", "Loading activity log...")}</p>
            </div>
          ) : activities.length > 0 ? (
            <div className="activity-timeline" style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {activities.map((activity, index) => (
                <motion.div
                  key={activity.id || index}
                  className="timeline-item d-flex mb-4"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="timeline-marker me-3">
                    <div className="timeline-dot bg-primary rounded-circle d-flex align-items-center justify-content-center"
                         style={{ width: '32px', height: '32px', minWidth: '32px' }}>
                      <FeatherIcon icon="clock" size={14} className="text-white" />
                    </div>
                  </div>
                  <div className="timeline-content flex-grow-1">
                    <div className="timeline-card bg-light rounded p-3">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <div className="fw-semibold text-dark">{activity.userName || 'System'}</div>
                          <div className="text-muted">{translateActivity(activity, t)}</div>
                        </div>
                        <Badge color="light" className="text-muted small">
                          <FeatherIcon icon="calendar" size={12} className="me-1" />
                          {formatCreatedDate(activity?.createdAt)}
                          {activity?.createdAt && (
                            <span className="ms-1">
                              {new Date(activity.createdAt).toLocaleTimeString(navigator.language || 'en-IN', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-5">
              <div className="empty-state-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                   style={{ width: '64px', height: '64px', backgroundColor: '#f8f9fa' }}>
                <FeatherIcon icon="clock" size={24} className="text-muted" />
              </div>
              <h5 className="text-muted">{t("business.components.service_requests.no_activity_recorded", "No activity recorded")}</h5>
              <p className="text-muted mb-0">{t("shared.activity_will_appear_here", "Activity will appear here as the ticket progresses")}</p>
            </div>
          )}

          {/* Comment Section */}
          <div className="comment-section mt-4 pt-4 border-top">
            <div className="d-flex align-items-center mb-3">
              <div className="info-icon me-3">
                <div className="bg-secondary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                     style={{ width: '32px', height: '32px' }}>
                  <FeatherIcon icon="message-circle" size={16} className="text-secondary" />
                </div>
              </div>
              <h6 className="mb-0 fw-bold">{t("shared.add_comment", "Add Comment")}</h6>
            </div>

            <Form onSubmit={handleCommentSubmit}>
              <div className="d-flex gap-3 align-items-start">
                <div className="comment-input flex-grow-1">
                  <Input
                    type="textarea"
                    placeholder={t("business.components.service_requests.add_comment_placeholder", "Add a comment to this ticket...")}
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    onKeyPress={handleCommentKeyPress}
                    rows={3}
                    className="border-0 shadow-sm"
                    style={{
                      resize: 'none',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '12px',
                      padding: '12px 16px'
                    }}
                    disabled={submittingComment}
                  />
                </div>
                <div className="comment-actions d-flex flex-column gap-2">
                  <Button
                    type="submit"
                    color="primary"
                    size="sm"
                    className="d-flex align-items-center gap-2"
                    disabled={!commentText.trim() || submittingComment}
                    style={{ minWidth: '100px' }}
                  >
                    {submittingComment ? (
                      <>
                        <Spinner size="sm" />
                        {t("shared.posting", "Posting...")}
                      </>
                    ) : (
                      <>
                        <FeatherIcon icon="send" size={14} />
                        {t("shared.post", "Post")}
                      </>
                    )}
                  </Button>
                  {commentText.trim() && (
                    <Button
                      type="button"
                      color="light"
                      size="sm"
                      onClick={() => setCommentText('')}
                      disabled={submittingComment}
                    >
                      <FeatherIcon icon="x" size={14} />
                    </Button>
                  )}
                </div>
              </div>
              <div className="comment-help mt-2">
                <small className="text-muted d-flex align-items-center">
                  <FeatherIcon icon="info" size={12} className="me-1" />
                  {t("shared.press_enter_to_submit_or_shift_enter_for", "Press Enter to submit or Shift+Enter for new line")}
                </small>
              </div>
            </Form>
          </div>
        </CardBody>
      </Card>
    </div>
  );

  return (
    <div className="page-content mt-4">
      {/* Header Actions */}
      <div className="mb-4 d-flex align-items-center gap-3">
        <motion.div
          className="mb-4 d-flex align-items-center gap-3"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <PermissionGuard permission="service-request:create">
            <button
              className="btn btn-success"
              onClick={() => toggleCreateModal()}
              title={t("business.components.service_requests.add_new", "Add new service request")}
            >
              <FeatherIcon icon="plus" size={18} className="me-1" />
              {t("shared.new_request", "New Request")}
            </button>
          </PermissionGuard>
          <div
            className="ms-auto"
            style={{ minWidth: "400px", flexGrow: 1, maxWidth: "500px" }}
          >
            <Input
              type="text"
              placeholder={t("shared.search_requests", "Search requests...")}
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
        </motion.div>
      </div>

      {/* Main Card */}
      <Card>
        <Nav pills className="justify-content-start px-3 pt-3 gap-2">
          {[
            { key: 'PENDING', label: t('shared.pending', 'Pending'), icon: 'clock' },
            { key: 'APPROVED', label: t('shared.approved', 'Approved'), icon: 'check-circle' },
            { key: 'DECLINED', label: t('shared.declined', 'Declined'), icon: 'x-circle' },
            { key: 'DONE', label: t('shared.done', 'Done'), icon: 'check-square' },
          ].map((tab) => (
            <NavItem key={tab.key}>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <NavLink
                  style={{ cursor: 'pointer' }}
                  className={`tab-btn ${statusFilter === tab.key ? 'active' : ''}`}
                  onClick={() => toggleTab(tab.key)}
                >
                  <FeatherIcon icon={tab.icon} className="me-1" />
                  {tab.label}
                </NavLink>
              </motion.div>
            </NavItem>
          ))}
        </Nav>
        <CardHeader>
          <div className="d-flex justify-content-between align-items-center">
            <h3 className="card-title text-success mb-0">
              {isAdmin ? t('shared.service_requests', 'Service Requests') : t('business.components.service_requests.my_requests', 'My Service Requests')}
            </h3>
          </div>
        </CardHeader>
        <CardBody>
          <div
            className="table-responsive"
            style={{
              overflowX: "auto",
              whiteSpace: "nowrap",
              maxHeight: "calc(100vh - 300px)",
              overflowY: "auto",
            }}
          >
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th style={{ whiteSpace: 'nowrap' }}>{t("shared.request_number", "Request Number")}</th>
                  <th>{t("shared.title", "Title")}</th>
                  <th>{t("shared.status", "Status")}</th>
                  <th>{t("shared.priority", "Priority")}</th>
                  <th>{t("shared.asset", "Asset")}</th>
                  <th>{t("shared.location", "Location")}</th>
                  {isAdmin && <th>{t("shared.requested_by", "Requested By")}</th>}
                  <th>{t("shared.created", "Created")}</th>
                  {statusFilter === 'PENDING' && <th>{t("shared.edit", "Edit")}</th>}
                  {statusFilter === 'PENDING' && <th>{t("shared.delete", "Delete")}</th>}
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {serviceRequests?.map((request, ind) => (
                    <motion.tr
                      key={request.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <th scope="row">{ind + 1}</th>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <span
                          className="text-primary cursor-pointer hover-underline"
                          onClick={() => toggleDetailsModal(request)}
                          style={{ cursor: 'pointer', fontWeight: 500 }}
                        >
                          {request.requestNumber || '-'}
                        </span>
                      </td>
                      <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={request.title}
                      >
                        {request.title}
                      </td>
                      <td>
                        {getStatusBadge(request.status)}
                      </td>
                      <td>
                        {request.urgencyLevel && (
                          <span className={`badge rounded-pill py-1 px-2 text-uppercase ${urgencyColors[request.urgencyLevel] || 'bg-secondary'}`}>
                            {request?.urgencyLevel ? t(`shared.${request?.urgencyLevel?.toLowerCase()}`, request?.urgencyLevel) : '-'}
                          </span>
                        )}
                      </td>
                      <td>
                        {request.asset ? (
                          <span
                            className="text-primary cursor-pointer hover-underline"
                            onClick={() => handleAssetClick(request.asset?.id, request.asset)}
                            style={{ cursor: 'pointer' }}
                          >
                            {request.asset.assetName}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        {request.locationName ? (
                          <span
                            className="text-primary cursor-pointer hover-underline"
                            onClick={() => handleLocationClick(request.locationName)}
                            style={{ cursor: 'pointer' }}
                          >
                            {request.locationName}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      {isAdmin && (
                        <td>{request.requestedBy?.name || '-'}</td>
                      )}
                      <td>
                        {formatCreatedDate(request.createdAt)}
                      </td>
                      {statusFilter === 'PENDING' && (
                        <td>
                          {canModifyRequest(request) && (
                            <PermissionGuard permission="service-request:update">
                              <span className='text-warning' role='button' onClick={() => toggleCreateModal(request)}>
                                <FeatherIcon icon="edit" />
                              </span>
                            </PermissionGuard>
                          )}
                        </td>
                      )}
                      {statusFilter === 'PENDING' && (
                        <td>
                          {canModifyRequest(request) && (
                            <PermissionGuard permission="service-request:delete">
                              <span className='text-danger' role='button' onClick={() => {
                                setSelectedRequest(request);
                                setDeleteModal(true);
                              }}>
                                <FeatherIcon icon="trash-2" />
                              </span>
                            </PermissionGuard>
                          )}
                        </td>
                      )}
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {/* Loading */}
          {loading && (
            <div className="text-center py-3">
              <Spinner color="primary" />
            </div>
          )}

          {/* Empty state */}
          {!loading && serviceRequests.length === 0 && (
            <div className="text-center py-5">
              <FeatherIcon icon="inbox" size={48} className="text-muted mb-3" />
              <p className="text-muted">{t("business.components.service_requests.no_requests_found", "No service requests found")}</p>
            </div>
          )}

          {/* Load More */}
          <div className="text-center mt-3">
            {hasMore && serviceRequests.length > 0 ? (
              <Button color="primary" onClick={loadMore} disabled={loading}>
                {loading ? 'Loading...' : 'Load More'}
              </Button>
            ) : (
              serviceRequests.length > 0 && (
                <div className="text-muted">{t("business.components.service_requests.no_more_requests", "No more requests to load")}</div>
              )
            )}
          </div>
        </CardBody>
      </Card>

      {/* Create/Edit Modal */}
      <ServiceRequestModal
        isOpen={createModal}
        toggle={() => toggleCreateModal()}
        requestData={selectedRequest}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        errors={errors}
        apiError={apiError}
        locations={locations}
        enumData={enumData}
      />

      {/* Details Modal */}
      <Modal isOpen={detailsModal} toggle={() => toggleDetailsModal()} size="lg">
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="modal-content border-0 shadow-lg"
          style={{ borderRadius: '12px', overflow: 'hidden' }}
        >
          {/* Enhanced Header */}
          <div
            className="modal-header border-0 text-white position-sticky d-flex justify-content-between align-items-center"
            style={{
              top: 0,
              zIndex: 10,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '1.5rem'
            }}
          >
            <div className="d-flex align-items-center">
              <div className="ticket-icon me-3">
                <div className="bg-white bg-opacity-20 rounded-circle d-flex align-items-center justify-content-center"
                     style={{ width: '48px', height: '48px' }}>
                  <FeatherIcon icon="file-text" size={24} className="text-white" />
                </div>
              </div>
              <div className="flex-grow-1">
                <h4 className="modal-title mb-1 text-white d-flex align-items-center">
                  <span className="me-2">{selectedRequest?.requestNumber || t('shared.request_details', 'Request Details')}</span>
                  <div className="d-flex align-items-center gap-2">
                    <Badge
                      color={
                        selectedRequest?.status === 'PENDING' ? 'warning' :
                        selectedRequest?.status === 'APPROVED' ? 'success' :
                        selectedRequest?.status === 'DECLINED' ? 'danger' :
                        selectedRequest?.status === 'DONE' ? 'info' : 'secondary'
                      }
                      className="badge-soft d-inline-flex align-items-center text-uppercase"
                      style={{ fontSize: '11px', padding: '4px 8px' }}
                    >
                      <FeatherIcon icon={srStatusOptions[selectedRequest?.status]?.icon || 'help-circle'} size={12} className="me-1" />
                      {t(`shared.${selectedRequest?.status?.toLowerCase()}`, selectedRequest?.status)}
                    </Badge>
                    {selectedRequest?.urgencyLevel && (
                      <Badge
                        color={
                          selectedRequest.urgencyLevel === 'HIGH' ? 'danger' :
                          selectedRequest.urgencyLevel === 'MEDIUM' ? 'warning' :
                          selectedRequest.urgencyLevel === 'LOW' ? 'success' :
                          selectedRequest.urgencyLevel === 'SEVERE' ? 'dark' : 'secondary'
                        }
                        className="badge-soft d-inline-flex align-items-center"
                        style={{ fontSize: '11px', padding: '4px 8px' }}
                      >
                        <FeatherIcon icon={
                          selectedRequest.urgencyLevel === 'HIGH' ? 'alert-triangle' :
                          selectedRequest.urgencyLevel === 'MEDIUM' ? 'alert-circle' :
                          selectedRequest.urgencyLevel === 'LOW' ? 'check-circle' : 'help-circle'
                        } size={12} className="me-1" />
                        {t(`shared.${selectedRequest?.urgencyLevel?.toLowerCase()}`, selectedRequest?.urgencyLevel)}
                      </Badge>
                    )}
                  </div>
                </h4>
                <div className="d-flex align-items-center gap-3">
                  <span className="text-white small">
                    <FeatherIcon icon="calendar" size={14} className="me-1" />
                    {formatCreatedDate(selectedRequest?.createdAt)}
                  </span>
                  <span className="text-white small">
                    <FeatherIcon icon="user" size={14} className="me-1" />
                    {selectedRequest?.requestedBy?.name || '-'}
                  </span>
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center gap-3 ms-auto">
              {selectedRequest?.status === 'PENDING' && canModifyRequest(selectedRequest) && (
                <button
                  type="button"
                  className="btn btn-sm d-flex align-items-center gap-2 px-3 py-2 border-0 shadow-sm"
                  onClick={() => {
                    toggleDetailsModal();
                    toggleCreateModal(selectedRequest);
                  }}
                  title={t("shared.edit_request", "Edit Request")}
                  style={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    color: '#495057',
                    fontWeight: '500',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 1)';
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.95)';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                  }}
                >
                  <FeatherIcon icon="edit-3" size={16} />
                  <span>{t("shared.edit", "Edit")}</span>
                </button>
              )}
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={() => toggleDetailsModal()}
                aria-label="Close"
              ></button>
            </div>
          </div>

          {/* Enhanced Navigation */}
          <Nav
            tabs
            className="border-bottom-0 px-4 bg-light"
          >
            <NavItem>
              <NavLink
                className={`nav-link-modern ${activeDetailsTab === 'details' ? 'active' : ''}`}
                onClick={() => setActiveDetailsTab('details')}
                style={{
                  border: 'none',
                  borderRadius: '8px 8px 0 0',
                  padding: '12px 20px',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
              >
                <FeatherIcon icon="file-text" size={16} className="me-2" />
                {t('shared.details', 'Details')}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={`nav-link-modern ${activeDetailsTab === 'activity' ? 'active' : ''}`}
                onClick={() => setActiveDetailsTab('activity')}
                style={{
                  border: 'none',
                  borderRadius: '8px 8px 0 0',
                  padding: '12px 20px',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
              >
                <FeatherIcon icon="activity" size={16} className="me-2" />
                {t('shared.activity', 'Activity')}
                {activities.length > 0 && (
                  <Badge color="primary" pill className="ms-2" style={{ fontSize: '10px' }}>
                    {activities.length}
                  </Badge>
                )}
              </NavLink>
            </NavItem>
          </Nav>

          <div className="modal-body p-0" style={{ maxHeight: '60vh', overflow: 'auto' }}>

            <TabContent activeTab={activeDetailsTab}>
              <TabPane tabId="details">
                <div className="p-4">
            {selectedRequest && (
              <Row className="g-4">
                <Col md={6}>
                  <div className="bg-light rounded p-3 h-100">
                    <h6 className="text-primary mb-3 fw-semibold">
                      <FeatherIcon icon="info" size={16} className="me-2" />
                      {t('shared.basic_information', 'Basic Information')}
                    </h6>
                    <div className="mb-3">
                      <label className="text-muted small">{t("shared.title", "Title")}</label>
                      <div className="fw-medium">{selectedRequest.title}</div>
                    </div>
                    <div className="mb-3">
                      <label className="text-muted small">{t("shared.status", "Status")}</label>
                      <div>
                        {getStatusBadge(selectedRequest.status)}
                      </div>
                    </div>
                    {selectedRequest.workOrderStatus && (
                      <div className="mb-3">
                        <label className="text-muted small">{t("shared.work_order_status", "Work Order Status")}</label>
                        <div>
                          {getWoStatusBadge(selectedRequest.workOrderStatus)}
                        </div>
                      </div>
                    )}
                    <div className="mb-3">
                      <label className="text-muted small">{t("shared.priority", "Priority")}</label>
                      <div>
                        {selectedRequest.urgencyLevel ? (
                          <span className={`badge rounded-pill py-1 px-2 text-uppercase ${urgencyColors[selectedRequest.urgencyLevel] || 'bg-secondary'}`}>
                            {selectedRequest?.urgencyLevel ? t(`shared.${selectedRequest?.urgencyLevel?.toLowerCase()}`, selectedRequest?.urgencyLevel) : '-'}
                          </span>
                        ) : '-'}
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="text-muted small">{t("shared.usage_status", "Usage Status")}</label>
                      <div className="fw-medium text-uppercase">
                        {selectedRequest.usageStatus ? t(`shared.${selectedRequest.usageStatus?.toLowerCase()}`, selectedRequest.usageStatus) : '-'}
                      </div>
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="bg-light rounded p-3 h-100">
                    <h6 className="text-primary mb-3 fw-semibold">
                      <FeatherIcon icon="map-pin" size={16} className="me-2" />
                      {t('shared.asset_location', 'Asset & Location')}
                    </h6>
                    <div className="mb-3">
                      <label className="text-muted small">{t("shared.asset", "Asset")}</label>
                      <div className="fw-medium">
                        {selectedRequest.asset ? (
                          <span
                            className="text-primary cursor-pointer hover-underline"
                            onClick={() => {
                              toggleDetailsModal();
                              handleAssetClick(selectedRequest.asset.id, selectedRequest.asset);
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            {selectedRequest.asset.assetName}
                            {selectedRequest.asset.serialNumber && (
                              <span className="text-muted small ms-2">
                                (SN: {selectedRequest.asset.serialNumber})
                              </span>
                            )}
                          </span>
                        ) : '-'}
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="text-muted small">{t("shared.location", "Location")}</label>
                      <div className="fw-medium">
                        {selectedRequest.locationName ? (
                          <span
                            className="text-primary cursor-pointer hover-underline"
                            onClick={() => {
                              toggleDetailsModal();
                              handleLocationClick(selectedRequest.locationName);
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            {selectedRequest.locationName}
                          </span>
                        ) : '-'}
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="text-muted small">{t("shared.requested_by", "Requested By")}</label>
                      <div className="fw-medium">
                        {selectedRequest.requestedBy?.name || '-'}
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="text-muted small">Created At</label>
                      <div className="fw-medium">{formatCreatedDate(selectedRequest.createdAt)}</div>
                    </div>
                    {selectedRequest.reviewedBy && (
                      <div>
                        <label className="text-muted small">Reviewed By</label>
                        <div className="fw-medium">{selectedRequest.reviewedBy.name || '-'}</div>
                      </div>
                    )}
                  </div>
                </Col>

                {/* Linked Work Order - shown when APPROVED */}
                {selectedRequest.status === 'APPROVED' && selectedRequest.workOrderTicketNumber && (
                  <Col md={12}>
                    <div className="rounded p-3" style={{ backgroundColor: '#d4edda' }}>
                      <h6 className="text-success mb-2 fw-semibold">
                        <FeatherIcon icon="check-circle" size={16} className="me-2" />
                        {t("shared.linked_work_order", "Linked Work Order")}
                      </h6>
                      <div className="d-flex align-items-center gap-3">
                        <div>
                          <label className="text-muted small">{t("shared.ticket_number", "Ticket Number")}</label>
                          <div className="fw-medium">{selectedRequest.workOrderTicketNumber}</div>
                        </div>
                        {selectedRequest.workOrderStatus && (
                          <div>
                            <label className="text-muted small">{t("shared.work_order_status", "Work Order Status")}</label>
                            <div>
                              {getWoStatusBadge(selectedRequest.workOrderStatus)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>
                )}

                {/* Decline Reason - shown when DECLINED */}
                {selectedRequest.status === 'DECLINED' && (
                  <Col md={12}>
                    <div className="rounded p-3" style={{ backgroundColor: '#f8d7da' }}>
                      <h6 className="text-danger mb-2 fw-semibold">
                        <FeatherIcon icon="x-circle" size={16} className="me-2" />
                        {t("shared.decline_reason", "Decline Reason")}
                      </h6>
                      <p className="mb-0">
                        {selectedRequest.declineReason || 'No reason provided'}
                      </p>
                    </div>
                  </Col>
                )}

                {selectedRequest.description && (
                  <Col md={12}>
                    <div className="bg-light rounded p-3">
                      <h6 className="text-primary mb-3 fw-semibold">
                        <FeatherIcon icon="file-text" size={16} className="me-2" />
                        {t("shared.description", "Description")}
                      </h6>
                      <p className="mb-0">{selectedRequest.description}</p>
                    </div>
                  </Col>
                )}
                {selectedRequest.imageUrls?.length > 0 && (
                  <Col md={12}>
                    <div className="bg-light rounded p-3">
                      <h6 className="text-primary mb-3 fw-semibold">
                        <FeatherIcon icon="image" size={16} className="me-2" />
                        {t("shared.images", "Images")}
                      </h6>
                      <div className="d-flex gap-2 flex-wrap">
                        {selectedRequest.imageUrls.map((url, idx) => (
                          <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                            <img
                              src={url}
                              alt={`Attachment ${idx + 1}`}
                              style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8 }}
                            />
                          </a>
                        ))}
                      </div>
                    </div>
                  </Col>
                )}
                {selectedRequest.documentUrls?.length > 0 && (
                  <Col md={12}>
                    <div className="bg-light rounded p-3">
                      <h6 className="text-primary mb-3 fw-semibold">
                        <FeatherIcon icon="paperclip" size={16} className="me-2" />
                        {t("shared.documents", "Documents")}
                      </h6>
                      <div className="d-flex gap-2 flex-wrap">
                        {selectedRequest.documentUrls.map((url, idx) => (
                          <a
                            key={idx}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-primary"
                          >
                            <FeatherIcon icon="file" size={14} className="me-1" />
                            {t("shared.document", "Document")} {idx + 1}
                          </a>
                        ))}
                      </div>
                    </div>
                  </Col>
                )}
              </Row>
            )}
                </div>
              </TabPane>
              <TabPane tabId="activity">
                {renderActivityTab()}
              </TabPane>
            </TabContent>
          </div>
          <div className="modal-footer border-top bg-light">
            {selectedRequest?.status === 'PENDING' && isAdmin && (
              <>
                <button
                  type="button"
                  className="btn btn-success d-flex align-items-center gap-1"
                  onClick={() => {
                    setDetailsModal(false);
                    setApproveModal(true);
                  }}
                >
                  <FeatherIcon icon="check" size={14} />
                  {t("shared.approve", "Approve")}
                </button>
                <button
                  type="button"
                  className="btn btn-danger d-flex align-items-center gap-1"
                  onClick={() => {
                    setDetailsModal(false);
                    setDeclineModal(true);
                  }}
                >
                  <FeatherIcon icon="x" size={14} />
                  {t("shared.decline", "Decline")}
                </button>
              </>
            )}
            <button type="button" className="btn btn-secondary" onClick={() => toggleDetailsModal()}>
              {t("shared.close", "Close")}
            </button>
          </div>
        </motion.div>
      </Modal>

      {/* Asset Modal */}
      <AssetModal
        isOpen={assetModal.isOpen}
        toggle={() => setAssetModal(prev => ({ ...prev, isOpen: false }))}
        userData={assetModal.userData}
        loading={assetModal.loading}
        formatCreatedDate={formatCreatedDate}
      />

      {/* Location Details Modal */}
      <LocationModal
        isOpen={locationModal.isOpen}
        toggle={() => setLocationModal(prev => ({ ...prev, isOpen: false }))}
        title={locationModal.title}
        mainData={locationModal.mainData}
        relatedData={locationModal.relatedData}
        loading={locationModal.loading}
        formatCreatedDate={formatCreatedDate}
      />

      {/* Approve Service Request Modal */}
      <ApproveServiceRequestModal
        isOpen={approveModal}
        toggle={() => {
          setApproveModal(false);
          setApiError("");
        }}
        serviceRequest={selectedRequest}
        onApprove={handleApprove}
        isSubmitting={isSubmitting}
        apiError={apiError}
        locations={locations}
        enumData={enumData}
        technicians={technicians}
        categories={categories}
        vendors={vendors}
        organizationId={organizationId}
      />

      {/* Decline Service Request Modal */}
      <DeclineServiceRequestModal
        isOpen={declineModal}
        toggle={() => {
          setDeclineModal(false);
          setApiError("");
        }}
        serviceRequest={selectedRequest}
        onDecline={handleDecline}
        isSubmitting={isSubmitting}
      />

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} centered>
        <div className="modal-header">
          <h5 className="modal-title">{t("shared.confirm_delete", "Confirm Delete")}</h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setDeleteModal(false)}
            aria-label="Close"
          />
        </div>
        <div className="modal-body">
          <p>{t("business.components.service_requests.confirm_delete_service_request", "Are you sure you want to delete this service request?")}</p>
          <p className="text-muted small mb-0">
            <strong>{t("shared.title", "Title")} :</strong> {selectedRequest?.title}
          </p>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={() => setDeleteModal(false)}>
            {t("shared.cancel", "Cancel")}
          </button>
          <button type="button" className="btn btn-danger" onClick={handleDelete}>
            {t("shared.delete", "Delete")}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ServiceRequests;
